import { Box, Button, Grid2 as Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import type { MaDeal } from '@/types/GraphqlSchema';
import { useUpdateDealDetails } from '../../hooks/useUpdateDealDetails';
import { AcquisitionCostSection } from './components/AcquisitionCostSection';
import { ConsiderationSection } from './components/ConsiderationSection';
import { EmissionsSection } from './components/EmissionsSection';
import { ProductionSection } from './components/ProductionSection';
import { ReservesSection } from './components/ReservesSection';
import { SummarySection } from './components/SummarySection';
import type { DealDetailsFormField, DealDetailsFormValue, DealDetailsFormValues } from './types';

type DealDetailsFormProps = {
  deal: MaDeal;
};

export const DealDetailsForm = ({ deal }: DealDetailsFormProps) => {
  const { setSnack } = useSnackBarContext();
  const [enableSave, setEnableSave] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState(() => {
    // Remove id and __typename from initial values
    const { id: _, __typename, ...initialValues } = deal.maDealDetails || {};
    return initialValues;
  });
  const { updateDealDetails, data: updateResult, loading, error } = useUpdateDealDetails();

  useEffect(() => {
    if (error) {
      setSnack({
        open: true,
        message: `Error updating deal details: ${error.message}`,
        severity: 'error',
      });
      setEnableSave(false);
    }
  }, [error, setSnack]);

  useEffect(() => {
    if (updateResult) {
      setSnack({
        open: true,
        message: 'Deal details updated successfully',
        severity: 'success',
      });
    }
  }, [updateResult, setSnack]);

  // Helper to check if value is numeric (allow empty string)
  const isNumeric = (value: DealDetailsFormValue) =>
    value === '' || (!Number.isNaN(Number(value)) && Number.isFinite(Number(value)));

  const validate = (field: DealDetailsFormField, value: DealDetailsFormValue) => {
    // For numeric fields, check if the value is a valid number
    const isInvalid = field.type === 'number' && !isNumeric(value);

    setErrors(prev => ({
      ...prev,
      [field.key]: isInvalid,
    }));
  };

  const onSave = (values: DealDetailsFormValues) => {
    setErrors({});
    setEnableSave(false);
    updateDealDetails(Number(deal.id), values);
  };

  const onChange = (field: DealDetailsFormField, value: DealDetailsFormValue) => {
    setEnableSave(true);
    // Validate the field
    validate(field, value);
    setValues((prev: DealDetailsFormValues) => ({ ...prev, [field.key]: value }));
  };

  const hasErrors = Object.values(errors).some(error => error);

  return (
    <Box
      component='form'
      onSubmit={e => {
        e.preventDefault();
        onSave(values);
      }}
      sx={{ mt: 2, position: 'relative' }}
    >
      <Box gap={3} display='flex' flexDirection='column' sx={{ padding: 2 }}>
        <SummarySection values={values} onChange={onChange} errors={errors} />
        <ConsiderationSection values={values} onChange={onChange} errors={errors} />
        <ReservesSection values={values} onChange={onChange} errors={errors} />
        <ProductionSection values={values} onChange={onChange} errors={errors} />
        <AcquisitionCostSection values={values} onChange={onChange} errors={errors} />
        <EmissionsSection values={values} onChange={onChange} errors={errors} />
        {/* Save Button */}
        <Grid size={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type='submit'
              sx={{ borderRadius: '2em', padding: '0.5em 2em' }}
              variant='contained'
              color='secondary'
              disabled={loading || !enableSave || hasErrors}
            >
              Save
            </Button>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};
